import { put, takeEvery } from 'redux-saga/effects';

import { selectTyped } from '@pressreader/appstore';
import { isStaleNavigation, navigateBegan, navigateDone, navigateInterrupted, selectCurrentNavId } from '@pressreader/navigation-core';
import { ViewName } from '@pressreader/navigation-types';

import { resetScrollUnlessRestoring } from './scroll.restore';

/**
 * Builds the navigation-completion pair for one view: a `navigateBegan` handler that emits the view's terminal
 * event, and the watcher saga that registers it. A view whose own runtime has no other reason to observe
 * navigation registers this pair instead of restating the supersession reasoning below.
 *
 * The saga scheduler receives `navigateBegan` only once the dispatch has propagated, so every other consumer
 * has already observed it before the handler runs.
 */
export function createNavigationCompletion(viewName: ViewName) {
    function* onNavigateBegan({ payload: { config, prevConfig } }: ReturnType<typeof navigateBegan>) {
        if (config.viewName !== viewName) {
            return;
        }

        // A redirect dispatched while `navigateBegan` was still propagating can already have superseded this
        // navigation. A stale terminal event would move focus and release queued interstitials for an unseen view.
        const currentNavId = yield* selectTyped(selectCurrentNavId);
        if (isStaleNavigation(config, currentNavId)) {
            yield put(navigateInterrupted({ reason: 'redirected', config }));
            return;
        }

        // Declares `scrollRestoration = 'manual'` so AppRefocus leaves the window alone, then tops the view unless a POP restores.
        yield* resetScrollUnlessRestoring();

        yield put(navigateDone({ prevConfig, config }));
    }

    function* runtimeSaga() {
        // `navigateBegan` is global, so takeLatest would let any other view's navigation cancel a pending
        // completion. Supersession is handled inside the handler, which emits `navigateInterrupted`.
        yield takeEvery(navigateBegan, onNavigateBegan);
    }

    return { onNavigateBegan, runtimeSaga };
}
